import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useWatch } from '@common-fe/common-fe';

import { WarnModal } from '@/components/elements';

import { useEditMode } from '../../hooks';
import { PayoutDefinitionField, PayoutDefinitionFormValues } from '../../PayoutDefinitionSetup.types';

import { useSavePayoutDefinitionStore } from './stores/useSavePayoutDefinition.store';
import { FinalSetup, SaveSuccessModal } from './components';
import { useSavePayoutDefinition } from './hooks';
import { useUpdatePayoutDefinitionQuery } from './queries';

const SERVER_ERROR_TITLE = 'The server encountered an error processing the request';
const SERVER_ERROR_TEXT = `It doesn't appear to have affected your data, but we cannot save the Payout Definition with the latest records. Our technical staff have been automatically notified and will be looking into this with the utmost urgency.`;

const FinalSetupContainer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { isEditMode, activateEditMode } = useEditMode();

  const payoutDefinitionName = useWatch<
    PayoutDefinitionFormValues,
    PayoutDefinitionField.payoutDefinitionName
  >({
    name: PayoutDefinitionField.payoutDefinitionName,
  });

  const { mutateAsync, isLoading: isMutating } = useUpdatePayoutDefinitionQuery(id);

  const {
    handleSave,
    isLoading,
    isSuccessModalVisible,
    isServerError,
    handleCloseSuccessModal,
    handleCloseErrorModal,
  } = useSavePayoutDefinition({ mutateAsync, isLoading: !!isMutating });

  const setHandleSave = useSavePayoutDefinitionStore((state) => state.setHandleSave);
  const setIsLoading = useSavePayoutDefinitionStore((state) => state.setIsLoading);

  useEffect(() => {
    setHandleSave(handleSave);
    return () => setHandleSave(null);
  }, [handleSave, setHandleSave]);

  useEffect(() => {
    setIsLoading(isLoading);
  }, [isLoading, setIsLoading]);

  return (
    <>
      <FinalSetup
        onSave={handleSave}
        isLoading={isLoading}
        isEditMode={isEditMode}
        onActivateEditMode={activateEditMode}
      />
      <SaveSuccessModal
        visible={isSuccessModalVisible}
        payoutDefinitionName={payoutDefinitionName}
        onClose={handleCloseSuccessModal}
      />
      <WarnModal
        testId="final-setup_server-error-modal"
        visible={isServerError}
        onSetVisible={handleCloseErrorModal}
        header={SERVER_ERROR_TITLE}
        helptext={SERVER_ERROR_TEXT}
        buttonText="Close"
        onSubmit={handleSave}
        submitButtonText="Try Again"
      />
    </>
  );
};

export default FinalSetupContainer;
